import { Asset } from "./assets";
import { Collection } from "./collections";
import { Color, PaginatedRecords } from "./common";
import { Contact } from "./contact";
import { Media, MediaFile } from "./media";
import { User } from "./users";

export interface DealRoom {
  id?: string;
  name?: string;
  description?: string;
  icon?: Media;
  banner?: DealRoomBanner;
  entityType?: string;
  assetsCount?: number;
  sectionsCount?: number;
  externalAssets?: DealRoomAsset[]; 
  customerName?: string;
  viewerType?: string;
  assetViewer?: string;
  totalPeopleInvolved? : number;
  customerLogo?: Media | MediaFile | null;
  stageValue?: string;
  sharedRecipientsCount?: number;
  collaboratorsCount?: number;
  winProbability?: number;
  createdDate?: Date;
  modifiedDate?: Date;

  live?: boolean;
  internal?: boolean;
  dealRoomTemplate?: boolean;
  dealRoom?: boolean;
  type?: string;
  opportunity?: string; 
  creator?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;

  assets?: DealRoomAsset[];
  sections?: DealRoomSection[];

  readAll?: boolean;
  writeAll?: boolean;
}

export interface DealRoomChangeRequest {
  name?: string;
  description?: string;
  banner?: DealRoomBanner;
  customerLogo?: string;
  companyLogo?: string;
  customerName?: string;
  viewerType?: string;
  assetViewer?: string;
  meetingLink?: string;
  assets?: DealRoomAssetChangeRequest[];
  sections?: DealRoomSectionChangeRequest[];
  deletedAssets?: string[];
  deletedSections?: string[];
  notifyRecipients?: boolean;
  subject?: string;
  message?: string;
  opportunity?: string;
}

export interface DealRoomSectionChangeRequest {
  id: string;
  name?: string;
  assets?: DealRoomAssetChangeRequest[];
  deletedAssets?: string[];
}

export interface DealRoomAssetChangeRequest {
  id: string;
  order: number;
  assetMetadata: any;
}

export interface FetchDealRoomsRequest {
  filter?: string;
  page: number;
  limit: number;
  sortBy?: string;
  orderBy?: string;
}

export interface FetchDealRoomsResponse {
  filter?: string;
  response: {
    records: DealRoom[];
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
  }
}

export interface assetsInitialState {
  loading: boolean,
  succeeded: boolean,
  error: any
  items: DealRoomAsset[],
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  }
}

export interface FetchDealRoomsErrorResponse {
  filter?: string;
  error: any
}


export interface DeleteDealRoomRequest {
  id: string;
}

export interface DeleteDealRoomResponse {
  id: string;
  response?: DealRoom;
}

export interface DeleteDealRoomErrorResponse {
  id: string;
  error: any
}

export interface UpdateDealRoomRequest {
  id: string;
  request: DealRoomChangeRequest;
}

export interface UpdateDealRoomResponse {
  response: DealRoom;
}

export interface UpdateDealRoomErrorResponse {
  id: string;
  error: any
}
export interface DealRoomBanner {
  position?: string;
  sharedByEnabled?: boolean;
  autoHeightEnabled?: boolean;
  background?: Media | MediaFile;
  bannerHeight?: string;
  descriptionOverlay?: Color;
  filter?: string;
  fontColor?: Color;
  overlayColor?: Color;
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  }
}

export interface DealRoomAsset{
  id: string;
  asset: Asset;
  order: number;
  metadata?: any;
}
export interface DealRoomEditState extends Omit<DealRoom, 'sections'> {
  sections?: DealRoomSectionState[]
}

export interface DealRoomEditRequest {
  dealRoom : DealRoomEditState,
  assets: assetsInitialState
}

export interface DealRoomSectionState {
  uid: string;
  loading: boolean,
  loaded: boolean,
  item: DealRoomSection | null,
  error: any,
}


export interface DealRoomSection {
  id?: string;
  name: string;
  
  dealRoom?: string;
  assets: assetsInitialState;
  createdDate?: Date;
  modifiedDate?: Date;
}


export interface FetchStakeholdersRequest{
  dealRoomId : string,
  type : StakeHolderTypeEnum,
  page : number,
}

export interface FetchStakeholdersErrorResponse {
  dealRoomId: string;
  type: StakeHolderTypeEnum;
  error: any;
}

export interface FetchStakeholdersResponse {
  dealRoomId: string;
  type: StakeHolderTypeEnum;
  response: any
}

export enum StakeHolderTypeEnum {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL'
}

export interface DeleteStakeholderRequest {
  dealRoomId : string,
  stakeholderType: StakeHolderTypeEnum;
  recipientId: string
}

export interface DeleteStakeholderResponse {
  dealRoomId: string;
}
export interface DeleteStakeholderErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface CreateDealRoomRequest {
  customerName?: string,
  description?: string,
  name: string,
  opportunity?: string
}

export interface CreateDealRoomResponse {
  response: DealRoom
}

export interface CreateDealRoomErrorResponse {
  error: any
}

export interface FetchDealRoomRequest {
  id: string;
}

export interface FetchDealRoomResponse {
  response: DealRoom
}

export interface FetchDealRoomErrorResponse {
  id: string
  error: any
}

export interface UpdateDealRoomPropertyRequest {
  dealRoom: string;
  property: string;
  value: any;
}


export interface AddAssetToDealRoomRequest {
  dealRoom: string;
  section?: number;
  sectionId?: string;
  assets: DealRoomAsset[];
  addAt: 'start' | 'end';
}

export interface ArrangeAssetInDealRoomRequest {
  dealRoom: string;
  section?: number;
  from: number;
  to: number;
}

export interface RearrangeAssetInDealRoomRequest {
  id: string;
  sectionIndex?: number;
  updatedAssets: DealRoomAsset[]
}

export interface MoveAssetToSectionRequest {
  dealRoom: string;
  source: number;
  target: number;
  asset: DealRoomAsset;
}

export interface RemoveAssetFromDealRoomRequest {
  dealRoom: string;
  section?: number;
  asset: string;
}


export interface UpdateAssetMetadataRequest {
  dealRoom: string;
  section?: number;
  updates: {
    id: string;
    metadata: any;
  }[];
}
// id, page, limit, assetCategory
export interface FetchDealRoomAssetsRequest {
  id: string;
  page: number;
  limit: number;
  assetCategory: AssetCatergory;
}

export interface FetchDealRoomAssetsResponse {
  id: string;
  assetCategory: AssetCatergory;
  response: {
    records: DealRoomAsset[];
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
  }
}

export interface FetchDealRoomAssetsErrorResponse {
  id: string;
  assetCategory: AssetCatergory;
  error: any
}

export enum AssetCatergory{ 
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
  ALL = 'ALL'
}

export interface FetchDealRoomAssetsForSectionRequest {
  id: string;
  page: number;
  limit: number;
  assetCategory: AssetCatergory;
  sectionId: string;
}

export interface FetchDealRoomAssetsForSectionResponse {
  id: string;
  assetCategory: AssetCatergory;
  response: any;
  sectionId: string;
}

export interface FetchDealRoomAssetsForSectionErrorResponse {
  id: string;
  assetCategory: AssetCatergory;
  error: any;
  sectionId: string;
}

export interface CreateDealRoomSectionRequest {
  dealRoom: string;
  name: string;
}

export interface RenameDealRoomSectionRequest {
  dealRoom: string;
  index: number;
  name: string;
}

export interface RemoveDealRoomSectionRequest {
  dealRoom: string;
  index: number;
}

export interface MoveDealRoomSectionRequest {
  dealRoom: string;
  from: number;
  to: number;
}

export interface FetchDealRoomCollaboratorsRequest {
  dealRoom: string;
  query: string,
  page: number;
}

export interface FetchDealRoomCollaboratorsResponse {
  dealRoom: string;
  response: {
    records: DealRoomCollaborate[];
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
  }
}

export interface FetchDealRoomCollaboratorsErrorResponse {
  dealRoom: string;
  error: any
}

export interface DealRoomCollaborate{
  entityType: 'user';
  user: User;
  read: boolean;
  write: boolean;
}

export interface UpdateDealRoomCollaboratorsRequest {
  dealRoom: string;
  requestBody: {
    readAll: boolean;
    writeAll: boolean;
    collaboratorsList: {
      id: string;
      write: boolean;
    }
  }
}

export interface UpdateDealRoomCollaboratorsResponse {
  dealRoom: string;
  response: DealRoomCollaborate[]
}
export interface UpdateDealRoomCollaboratorsErrorResponse {
  dealRoom: string;
  error: any
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
  hasMoreItems: boolean;
}

export interface RecordsState<T> {
  items: T[];
  pagination: Pagination;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export interface DealRoomSharedCollectionsState {
  items: Collection[];
  loading: boolean;
  loaded: boolean;
  error: any;
}

export interface DealRoomDetailsData {
  id: string;
  title: string;
  value: number;
  owner: string;
  stage: string;
  prediction: number;
  remainingDays: number | null;
  closedDate: number;
  createdDate: number;
  integrationType: string;
  lastActivityDate: number;
}

export interface DealRoomDetailsState {
  data: DealRoomDetailsData | null;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export interface DealRoomSignal {
  priority: string;
  subject: string;
  message: string;
}

export interface DealRoomHistory {
  id: string;
  dealId: string;
  stageValue: string;
  status: string;
  teamConfidence: number;
  aiConfidence: number;
  createdDate: number;
  createdDateStr: string;
}

export interface FetchDealRoomSharedAssetsRequest {
  dealRoomId: string;
  dealId: string;
  page: number;
}

interface SharedAssetsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: Asset[];
  totalRecordsCount: number;
}

export interface FetchDealRoomSharedAssetsResponse {
  dealRoomId: string;
  response: SharedAssetsResponse;
} 

export interface FetchDealRoomSharedAssetsErrorResponse {
  dealRoomId: string;
  error: any;
}
  
export interface FetchDealRoomSharedCollectionsRequest {
  dealRoomId: string;
  dealId: string;
}

export interface FetchDealRoomSharedCollectionsResponse {
  dealRoomId: string;
  response: Collection[];
} 

export interface FetchDealRoomSharedCollectionsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomDetailsRequest {
  dealRoomId: string;
  dealId: string;
}

export interface FetchingDealRoomDetailsResponse {
  dealRoomId: string;
  response: DealRoomDetailsData;
} 

export interface FetchingDealRoomDetailsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomSignalsRequest {
  dealRoomId: string;
  dealId: string;
  page: number;
}

interface SignalsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: DealRoomSignal[];
}

export interface FetchingDealRoomSignalsResponse {
  dealRoomId: string;
  response: SignalsResponse;
} 

export interface FetchingDealRoomSignalsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomHistoryRequest {
  dealRoomId: string;
  dealId: string;
  page: number;
}

interface HistoryResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: DealRoomHistory[];
}

export interface FetchingDealRoomHistoryResponse {
  dealRoomId: string;
  response: HistoryResponse;
} 

export interface FetchingDealRoomHistoryErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomEngageChatUsersRequest {
  dealRoomId: string;
  page: number;
}

interface DealRoomEngageChatUsersResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
  records: DealRoomEngageChatUser[];
}

export interface FetchingDealRoomEngageChatUsersResponse {
  dealRoomId: string;
  response: DealRoomEngageChatUsersResponse;
}

export interface FetchingDealRoomEngageChatUsersErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface DealRoomEngageChatUser {
  accessRemovedDate: number | null;
  active: boolean;
  contact: any;
  deliveryStatus: "sent" | "not-sent";
  events: any[];
  ndaConsent: null;
  shareType: "primary" | "secondary";
  sharedDate: number;
  timeSpent: number | null;
  type: any;
  visitor: any;
  lastOnline: string;
  online: boolean;
}

export interface FetchDealRoomActiveChatUserRequest {
  chatUserId: string;
  dealRoomId: string;
}

export interface FetchingDealRoomActiveChatUserResponse {
  dealRoomId: string;
  response: DealRoomEngageChatUser;
}

export interface FetchingDealRoomActiveChatUserErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomSmartCardsRequest {
  dealRoomId: string;
}

export interface FetchDealRoomSmartCardsResponse {
  dealRoomId: string;
  response: any;
}

export interface FetchDealRoomSmartCardsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomAssetAnalyticsRequest {
  dealRoomId: string;
  assetId: string;
  assetCategory: AssetCatergory;
  sectionId?: string;
}

export interface FetchDealRoomAssetAnalyticsResponse {
  dealRoomId: string;
  assetId: string;
  response: any;
  assetCategory: AssetCatergory;
  sectionId?: string;
}

export interface FetchDealRoomAssetAnalyticsErrorResponse {
  dealRoomId: string;
  assetId: string;
  error: any;
  assetCategory: AssetCatergory;
  sectionId?: string;
}

export interface FetchDealRoomEventsRequest {
  dealRoomId: string;
  request: {
    share: string;
    eventTypes: any[];
    assets: any[];
    contacts: any[];
  };
  page?: number;
}

export interface DealRoomEvents {
  id: string;
  type: string;
  createdDate: number;
  contact: Contact;
  target: Contact;
  visitor: any;
  session: string | null;
  visitorSession: string | null;
  sharedWith: any;
  assetAdded: any;
}

export interface FetchDealRoomEventsResponse {
  dealRoomId: string;
  request: {
    share: string;
    eventTypes: any[];
    assets: any[];
    contacts: any[];
  };
  data: PaginatedRecords<DealRoomEvents>;
}

export interface FetchDealRoomEventsErrorResponse {
  dealRoomId: string;
  request: {
    share: string;
    eventTypes: any[];
    assets: any[];
    contacts: any[];
  };
  error: any;
}